<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAudiencesStore } from 'dashboard/stores/audiences';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useContactLabelsStore } from 'dashboard/stores/contactLabels';
import AudienceContactCard from './AudienceContactCard.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

const ITEMS_PER_PAGE = 15;

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const audiencesStore = useAudiencesStore();
const contactLabelsStore = useContactLabelsStore();

const dialogRef = ref(null);
const audience = ref(null);
const contacts = ref([]);
const totalItems = ref(0);
const currentPage = ref(1);
const loadError = ref('');
let latestFetch = 0;

const isFetching = computed(() => audiencesStore.uiFlags.fetchingContacts);
const labelColors = computed(() => contactLabelsStore.getLabelColorMap);

// Only the latest request may fill the list (quick paging or opening another audience).
const fetchContacts = async (page = 1) => {
  latestFetch += 1;
  const fetchId = latestFetch;
  loadError.value = '';
  try {
    const result = await audiencesStore.fetchContacts({
      id: audience.value.id,
      page,
    });
    if (fetchId !== latestFetch) return;
    contacts.value = result.contacts;
    totalItems.value = result.count;
    currentPage.value = result.currentPage;
  } catch (error) {
    if (fetchId !== latestFetch) return;
    contacts.value = [];
    loadError.value = error.message;
  }
};

const open = selectedAudience => {
  audience.value = selectedAudience;
  contacts.value = [];
  loadError.value = '';
  dialogRef.value.open();
  contactLabelsStore.fetchAll();
  fetchContacts();
};

const openContact = contactId => {
  dialogRef.value.close();
  router.push({
    name: 'contacts_edit',
    params: { accountId: route.params.accountId, contactId },
  });
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    overflow-y-auto
    :title="audience?.name"
    :show-confirm-button="false"
    :cancel-button-label="t('AUDIENCES.CONTACTS.CLOSE')"
  >
    <div class="h-[60vh] -mx-6 px-6 overflow-y-auto">
      <div
        v-if="isFetching"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>
      <div
        v-else-if="loadError"
        class="flex flex-col items-center justify-center gap-1 py-10 text-center"
      >
        <span class="text-base text-n-slate-11">
          {{ t('AUDIENCES.CONTACTS.ERROR') }}
        </span>
        <span class="text-xs text-n-slate-10">{{ loadError }}</span>
      </div>
      <div
        v-else-if="!contacts.length"
        class="flex items-center justify-center py-10"
      >
        <span class="text-base text-n-slate-11">
          {{ t('AUDIENCES.CONTACTS.EMPTY_STATE') }}
        </span>
      </div>
      <div v-else class="flex flex-col gap-4">
        <AudienceContactCard
          v-for="contact in contacts"
          :key="contact.id"
          :contact="contact"
          :label-colors="labelColors"
          @open="openContact"
        />
      </div>
    </div>
    <template #footer>
      <div class="flex flex-col gap-4">
        <PaginationFooter
          v-if="!isFetching && contacts.length"
          current-page-info="AUDIENCES.CONTACTS.PAGINATION_SHOWING"
          :current-page="currentPage"
          :total-items="totalItems"
          :items-per-page="ITEMS_PER_PAGE"
          class="!px-0 !bg-transparent before:hidden"
          @update:current-page="fetchContacts"
        />
        <Button
          variant="faded"
          color="slate"
          type="button"
          :label="t('AUDIENCES.CONTACTS.CLOSE')"
          class="w-full"
          @click="dialogRef.close()"
        />
      </div>
    </template>
  </Dialog>
</template>
